import { FC, useState } from 'react'

import type { Row } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable.ts'
import type { DialogProps } from '@/dialogs/types'
import { deleteScriptsCategoryFx } from '@/models/scripts/categories.ts'
import { deleteScriptFx, unexpandFolder } from '@/models/scripts/scripts.ts'
import type { ScriptNode } from '@/modules/scripts/types.ts'

interface Props extends DialogProps {
  row: Row<Entry<ScriptNode>>
  onRemove: () => void
}

export const RemoveScriptsFolderDialog: FC<Props> = ({
  row,
  isOpen,
  onClose,
  onRemove,
}) => {
  const isDeletingCategory = useUnit(deleteScriptsCategoryFx.pending)
  const isDeletingScript = useUnit(deleteScriptFx.pending)

  // if no scripts array field - it's a script item
  const isScript = row.original._type === 'file'

  const dialogDescription = isScript
    ? `This action cannot be undone. This will permanently remove the
  following file: "${row.original.name}"`
    : `This action cannot be undone. This will permanently remove the
  following folder: "${row.original.name}" and all of the files in it.`

  const [isDeletingScripts, setIsDeletingScripts] = useState(false)

  const showDeleteScriptsCheckbox =
    !isScript && row.original.scripts?.length !== 0

  const removeRow = async (): Promise<void> => {
    const { id, original } = row

    // eslint-disable-next-line no-unused-expressions
    isScript
      ? await deleteScriptFx(original.id)
      : await deleteScriptsCategoryFx({
          id: original.id,
          contentPolicy: isDeletingScripts ? 'DELETE' : 'UNLINK',
        })

    onRemove()
    unexpandFolder(id)
    onClose()
  }

  function handleCheckedChange(checked: boolean) {
    setIsDeletingScripts(checked)
  }

  return (
    <Dialog show={isOpen}>
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description className="text-center">
          {dialogDescription}
        </Dialog.Description>
      </Dialog.Header>
      <div>
        {showDeleteScriptsCheckbox && (
          <label>
            <Checkbox
              checked={isDeletingScripts}
              onCheckedChange={handleCheckedChange}
            />
            <span className="ml-3 text-secondary-500 dark:text-secondary-200">
              Delete scripts in folder
            </span>
          </label>
        )}
      </div>
      <Dialog.ClosableFooter
        className="mt-6"
        onClose={onClose}
      >
        <Button
          variant="destructive"
          onClick={removeRow}
          isLoading={isDeletingCategory || isDeletingScript}
          testId="remove-folder-button"
        >
          Remove
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
